import React, { useState, useEffect, useCallback } from 'react';
import { FaTrash } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider';
import { ConfirmAlert, showAlert } from '../../../components';

const ProductList = ({ preview }) => {
  const {
    proposal,
    setProductForEdit,
    setProposal,
    setAlternativeValue,
    setShowAlternateProductSection,
    editProdIndex,
    setEditProdIndex,
  } = useProposal();

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    return () => {
      setDraggedIndex(null);
      setIsDragging(false);
    };
  }, []);

  useEffect(() => {
    if (editProdIndex !== null) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [editProdIndex]);

  // Removes the selected main product by index
  const removeProduct = useCallback(
    async (index) => {
      const result = await ConfirmAlert({
        confirmBtnText: 'Remove Product',
      });
      if (!result.isConfirmed) {
        showAlert('error', 'Product remove cancelled');
        return;
      }
      const updatedProducts = proposal.quoteProducts.filter(
        (_, i) => i !== index,
      );
      setProposal({ ...proposal, quoteProducts: updatedProducts });
      showAlert('success', 'Product removed successfully');
    },
    [proposal, setProposal],
  );

  const handleEditProduct = useCallback(
    (index) => {
      const product = proposal.quoteProducts[index];

      setProductForEdit(product);
      setEditProdIndex(index);
      if (product.alternateProduct) {
        setAlternativeValue('Yes');
        setShowAlternateProductSection(true);
      }
    },
    [
      proposal,
      setProductForEdit,
      setEditProdIndex,
      setAlternativeValue,
      setShowAlternateProductSection,
    ],
  );

  const handleDragStart = useCallback(
    (index) => {
      if (editProdIndex === null) {
        setDraggedIndex(index);
        setIsDragging(true);
      }
    },
    [editProdIndex],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (index) => {
      if (draggedIndex !== null && draggedIndex !== index) {
        const updatedProducts = [...proposal.quoteProducts];
        const [draggedProduct] = updatedProducts.splice(draggedIndex, 1);
        updatedProducts.splice(index, 0, draggedProduct);
        setProposal({ ...proposal, quoteProducts: updatedProducts });
        setDraggedIndex(null);
        setIsDragging(false);
      }
    },
    [draggedIndex, proposal, setProposal],
  );

  const renderProductRow = useCallback(
    (product, index, isAlternate = false) => {
      const {
        itemCode,
        itemDescription,
        productGroup,
        unit,
        managedRange,
        avgCost,
        taxCode,
        comment,
      } = isAlternate ? product.alternateProduct : product.mainProduct;
      const {
        mainProductCurrentSupplierExcGstPrice,
        mainProductTotal,
        mainProductCurrentSupplierGp,
        mainProductJaybelExcGstPrice,
        mainProductJaybelExcGstPriceTotal,
        mainProductJaybelIncGstPriceTotal,
        mainProductGpRequired,
        mainProductSaving,
        mainProductQuantity,
        mainProductCurrentSupplierIncGstPrice,
        mainProductCurrentSupplierIncGstPriceTotal,
        mainProductJaybelIncGstPrice,
      } = product;
      // Determine number of columns (with or without Actions)
      const numColumns = !isAlternate && !preview ? 17 : 16;
      // Use proposal.priceGstInclude to determine which totals to show
      const isGstInclusive = proposal?.priceGstInclude;
      // For main product
      const currentSupplierTotal = isGstInclusive
        ? mainProductCurrentSupplierIncGstPriceTotal
        : (product.mainProductCurrentSupplierExcGstTotal ??
          mainProductCurrentSupplierExcGstPrice);
      const jaybelPriceTotal = isGstInclusive
        ? mainProductJaybelIncGstPriceTotal
        : mainProductJaybelExcGstPriceTotal;
      // For alternate product
      const altCurrentSupplierTotal = isGstInclusive
        ? product.alternateProductCurrentSupplierIncGstPriceTotal
        : (product.alternateProductCurrentSupplierExcGstTotal ??
          product.mainProductCurrentSupplierExcGstPrice);
      const altJaybelPriceTotal = isGstInclusive
        ? product.alternateProductJaybelIncGstPriceTotal
        : product.alternateProductJaybelExcGstPriceTotal;
      return (
        <>
          <tr
            draggable={editProdIndex === null && !isAlternate}
            onDragStart={() => !isAlternate && handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={() => !isAlternate && handleDrop(index)}
            className={`
              text-sm border-b border-gray-200
              ${isAlternate ? 'bg-red-100' : 'bg-white'}
              ${draggedIndex === index && !isAlternate ? 'border-2 border-blue-500' : ''}
              ${isDragging && !isAlternate ? 'cursor-grabbing' : 'cursor-grab'}
            `}
            style={{
              opacity: draggedIndex === index && !isAlternate ? 0.5 : 1,
            }}>
            <td className="px-2 py-2">{index + 1}</td>
            <td className="px-2 py-2 text-xs">{itemCode}</td>
            <td className="px-2 py-2 text-sm min-w-40">{itemDescription}</td>
            <td className="px-2 py-2">{productGroup?.productGroupCode}</td>
            <td className="px-2 py-2">{unit}</td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.alternateProductQuantity
                : mainProductQuantity}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.mainProductCurrentSupplierExcGstPrice
                : mainProductCurrentSupplierExcGstPrice}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.mainProductCurrentSupplierIncGstPrice
                : mainProductCurrentSupplierIncGstPrice}
            </td>
            <td className="px-2 py-2">
              {isAlternate ? altCurrentSupplierTotal : currentSupplierTotal}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.alternateProductCurrentSupplierGp
                  ? `${product.alternateProductCurrentSupplierGp}%`
                  : '-'
                : mainProductCurrentSupplierGp
                  ? `${mainProductCurrentSupplierGp}%`
                  : '-'}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.alternateProductJaybelExcGstPrice
                : mainProductJaybelExcGstPrice}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.alternateProductJaybelIncGstPrice
                : mainProductJaybelIncGstPrice}
            </td>
            <td className="px-2 py-2">
              {isAlternate ? altJaybelPriceTotal : jaybelPriceTotal}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.alternateProductGpRequired
                  ? `${product.alternateProductGpRequired}%`
                  : product.alternateProductGpRequired
                : mainProductGpRequired
                  ? `${mainProductGpRequired}%`
                  : mainProductGpRequired}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.alternateProductSaving
                  ? `${product.alternateProductSaving}%`
                  : product.alternateProductSaving
                : mainProductSaving
                  ? `${mainProductSaving}%`
                  : mainProductSaving}
            </td>
            <td className="px-2 py-2">
              {isAlternate
                ? product.alternateProduct.taxCode === 'E'
                  ? 'E'
                  : '-'
                : taxCode === 'E'
                  ? 'E'
                  : '-'}{' '}
            </td>
            {!isAlternate && !preview && (
              <td className="px-2 py-2 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditProduct(index)}
                    title="Edit"
                    disabled={editProdIndex !== null}>
                    <MdEdit
                      className={`text-lg ${
                        editProdIndex !== null
                          ? 'text-gray-400'
                          : 'text-blue-500 hover:text-blue-700'
                      }`}
                    />
                  </button>
                  <button
                    className={`${
                      editProdIndex !== null
                        ? 'text-gray-400'
                        : 'text-red-500 hover:text-red-700'
                    }`}
                    onClick={() => removeProduct(index)}
                    title="Delete"
                    disabled={editProdIndex !== null}>
                    <FaTrash className="text-lg" />
                  </button>
                </div>
              </td>
            )}
          </tr>
          {/* Comment row for main product only, with correct colspan */}
          {!isAlternate && product.comment && (
            <tr className="bg-gray-100 text-xs border-b border-gray-200 ">
              <td colSpan={numColumns} className="px-2 py-2 text-left">
                <strong>Comment :</strong> {product.comment}
              </td>
            </tr>
          )}
        </>
      );
    },
    [
      handleDragStart,
      handleDragOver,
      handleDrop,
      handleEditProduct,
      removeProduct,
      draggedIndex,
      isDragging,
      editProdIndex,
      preview,
    ],
  );

  return (
    <div className="container mx-auto mb-5 mt-5 pr-5">
      {proposal.quoteProducts.length === 0 ? (
        <div className="text-center mt-5 text-2xl font-bold text-gray-600">
          No products available. <br />
          Please add a product.
        </div>
      ) : (
        <div
          className={`relative ${
            proposal.quoteProducts.length > 8 ||
            proposal.quoteProducts.length * 50 > window.innerHeight * 0.7
              ? 'max-h-[70vh] overflow-y-auto'
              : ''
          }`}>
          <table className="min-w-full bg-white border-gray-200">
            <thead
              className={`bg-gray-200 font-400 text-left min-w-full ${
                proposal.quoteProducts.length > 8 ||
                proposal.quoteProducts.length * 50 > window.innerHeight * 0.7
                  ? 'sticky top-0 z-10'
                  : ''
              }`}>
              <tr>
                {[
                  'Sr No',
                  'Prod Code',
                  'Prod Name',
                  'Prod Group',
                  'UOM',
                  'Quantity',
                  'Current Supplier Price Exc',
                  'Current Supplier Price Inc',
                  proposal?.priceGstInclude
                    ? 'Current Supplier Total (Inc)'
                    : 'Current Supplier Total (Exc)',
                  'Supplier GP (%)',
                  'Jaybel Price Exc',
                  'Jaybel Price Inc',
                  proposal?.priceGstInclude
                    ? 'Jaybel Price Total (Inc)'
                    : 'Jaybel Price Total (Exc)',
                  'Jaybel GP (%)',
                  'Savings (%)',
                  'GST Exempt',
                  'Actions',
                ].map((header) => (
                  <th
                    key={header}
                    className="px-2 py-3 border-b text-left text-xs font-medium text-gray-500 uppercase">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-center min-w-full font-600">
              {proposal.quoteProducts.map((product, index) => (
                <React.Fragment key={index}>
                  {/* Main Product Row */}
                  {renderProductRow(product, index)}

                  {/* Alternate Product Row (if exists) */}
                  {product.alternateProduct &&
                    renderProductRow(product, index, true)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductList;
